import { createSlice } from '@reduxjs/toolkit'
import { startOfToday, subDays, format } from 'date-fns'

import { DEFAULT_DATERANGE_FORMAT } from 'containers/TextAnalysis/Filters/DateRangeFilter/Helper'
import {
  fetchContributions,
  fetchAllTags,
  newContributionTags,
  updateContributionTags,
  bulkTags,
  getSingleContribution,
} from 'redux/thunks/TextAnalysis'
import {
  TextAnalysisStateProps,
  PaginationRowsOptions,
} from 'types/TextAnalysis'

const initialState: TextAnalysisStateProps = {
  data: [],
  total: 0,
  selectedContributions: [],
  contributions: {
    loading: false,
    bulkUpdate: {
      pending: false,
      done: false,
      error: '',
    },
    singleContribution: {
      pending: false,
      done: false,
      error: '',
      data: {},
    },
  },
  tags: {
    tagsList: [],
    fetchingTags: false,
    doneFetchingTags: false,
    fetchTagsError: '',
  },
  allContributions: {
    fetching: false,
    doneFetching: false,
    fetchError: '',
  },
  createContributionTags: {
    creatingTags: false,
    doneCreatingTags: false,
    creatingTagsError: false,
  },
  updateContributionTags: {
    updatingTags: false,
    doneUpdatingTags: false,
    updatingTagsError: false,
  },
  queries: {
    page: 1,
    per_page: PaginationRowsOptions[0],
    search: {},
  },
  userFilters: {
    created_at: {
      start_date: format(subDays(startOfToday(), 7), DEFAULT_DATERANGE_FORMAT),
      end_date: format(startOfToday(), DEFAULT_DATERANGE_FORMAT),
    },
  },
  hasUserFiltersUpdated: false,
}

export const textAnalysis = createSlice({
  name: 'textAnalysis',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.contributions.loading = action?.payload
    },
    resetPage: (state) => {
      state.queries = initialState.queries
    },
    setPage: (state, action) => {
      state.queries.page = action?.payload
    },
    setPerPage: (state, action) => {
      state.queries.per_page = action?.payload
    },
    setSearchString: (state, action) => {
      state.queries.search = action?.payload
    },
    resetAllTags: (state) => {
      state.tags = initialState.tags
    },
    updateAllTags: (state, action) => {
      state.tags.tagsList.push(action.payload)
    },
    resetContributionTags: (state) => {
      state.createContributionTags = initialState.createContributionTags
      state.updateContributionTags = initialState.updateContributionTags
    },
    setContributionTags: (state, action) => {
      const { index, newTags } = action.payload
      if (state.data[index]) state.data[index].tags = newTags
    },
    addSelectedContribution: (state, action) => {
      state.selectedContributions.push(action.payload)
    },
    clearSelectedContributions: (state, action) => {
      state.selectedContributions = [...action.payload]
    },
    selectAllContributions: (state) => {
      state.selectedContributions = [...state.data]
    },
    resetSelectedContributions: (state) => {
      state.selectedContributions = initialState.selectedContributions
    },
    setUserFilters: (state, action) => {
      state.userFilters = { ...state.userFilters, ...action.payload }
      state.hasUserFiltersUpdated = true
    },
    resetUserFilters: (state) => {
      state.userFilters = initialState.userFilters
      state.hasUserFiltersUpdated = true
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContributions.pending, (state) => {
        state.allContributions.fetching = true
        state.allContributions.doneFetching = false
      })
      .addCase(fetchContributions.fulfilled, (state, action) => {
        state.allContributions.fetching = false
        state.allContributions.doneFetching = true
        state.data = action?.payload?.data || []
        state.total = action?.payload?.meta?.total || 0
        state.hasUserFiltersUpdated = false
      })
      .addCase(fetchContributions.rejected, (state, action) => {
        state.allContributions.fetching = false
        state.allContributions.fetchError = action?.payload
        state.allContributions.doneFetching = false
      })
      .addCase(fetchAllTags.pending, (state) => {
        state.tags.fetchingTags = true
      })
      .addCase(fetchAllTags.fulfilled, (state, action) => {
        state.tags.tagsList = action?.payload?.tags || []
        state.tags.fetchingTags = false
        state.tags.doneFetchingTags = true
      })
      .addCase(fetchAllTags.rejected, (state, action) => {
        state.tags.fetchingTags = false
        state.tags.fetchTagsError = action?.payload
        state.tags.doneFetchingTags = false
      })
      .addCase(newContributionTags.pending, (state) => {
        state.createContributionTags.creatingTags = true
      })
      .addCase(newContributionTags.fulfilled, (state) => {
        state.createContributionTags.creatingTags = false
        state.createContributionTags.doneCreatingTags = true
      })
      .addCase(newContributionTags.rejected, (state) => {
        state.createContributionTags.creatingTags = false
        state.createContributionTags.creatingTagsError = true
        state.createContributionTags.doneCreatingTags = false
      })
      .addCase(updateContributionTags.pending, (state) => {
        state.updateContributionTags.updatingTags = true
      })
      .addCase(updateContributionTags.fulfilled, (state) => {
        state.updateContributionTags.updatingTags = false
        state.updateContributionTags.doneUpdatingTags = true
      })
      .addCase(updateContributionTags.rejected, (state) => {
        state.updateContributionTags.updatingTags = false
        state.updateContributionTags.updatingTagsError = true
      })
      .addCase(bulkTags.pending, (state) => {
        const { bulkUpdate } = state.contributions
        bulkUpdate.pending = true
        bulkUpdate.done = false
        bulkUpdate.error = ''
      })
      .addCase(bulkTags.fulfilled, (state) => {
        const { bulkUpdate } = state.contributions
        bulkUpdate.done = true
        bulkUpdate.pending = false
      })
      .addCase(bulkTags.rejected, (state, action) => {
        const { bulkUpdate } = state.contributions
        bulkUpdate.error = action?.payload || 'Error occurred'
        bulkUpdate.pending = false
      })
      .addCase(getSingleContribution.pending, (state) => {
        const { singleContribution } = state.contributions
        singleContribution.pending = true
        singleContribution.done = false
        singleContribution.error = ''
        singleContribution.data = {}
      })
      .addCase(getSingleContribution.fulfilled, (state, action) => {
        const { singleContribution } = state.contributions
        singleContribution.pending = false
        singleContribution.done = true
        singleContribution.data = action?.payload?.data[0] || {}
      })
      .addCase(getSingleContribution.rejected, (state, action) => {
        const { singleContribution } = state.contributions
        singleContribution.pending = false
        singleContribution.error = action?.payload || 'Error occurred'
      })
  },
})

const { actions, reducer } = textAnalysis
export const {
  setLoading,
  resetPage,
  setPage,
  setPerPage,
  setSearchString,
  resetAllTags,
  updateAllTags,
  resetContributionTags,
  setContributionTags,
  addSelectedContribution,
  clearSelectedContributions,
  selectAllContributions,
  resetSelectedContributions,
  setUserFilters,
  resetUserFilters,
} = actions
export default reducer
